import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * useDisputifierInvoices — Billing epic Phase C (SOUND-447).
 *
 * Backs the /billing/disputifier page. Two fetches against the Phase B API:
 *   - GET /billing/disputifier            -> filtered/sorted/paginated invoices
 *   - GET /billing/disputifier/summary    -> KPI aggregates (de-duped outstanding)
 *
 * Server-side contract (Phase B):
 *   GET /billing/disputifier?status=&search=&merchant=&sortBy=&sortDir=&page=&limit=
 *     -> { data: [invoice], pagination: { page, limit, total, totalPages } }
 *   GET /billing/disputifier/summary
 *     -> { outstanding, outstandingOpenCount, paidAllTime, paidCount,
 *          oldestOpenAgeDays, draftCount, sharedInvoiceCount, merchantsWithInvoices }
 *
 * NOTE: amounts are WHOLE DOLLARS — never /100 (Disputifier quirk).
 *
 * The provider is a parameter so future providers (NMI, own invoices) can reuse
 * this hook by passing `provider`.
 */
// UI column keys -> Phase B sort whitelist. Columns not listed pass through
// unchanged (status, amountDue, amountRemaining, invoiceDate already match).
const UI_TO_API_SORT = {
  merchant: "merchantName",
  invoice: "providerInvoiceId",
  age: "invoiceDate", // age is the inverse of invoiceDate — direction flipped below
};

export function useDisputifierInvoices({
  provider = "disputifier",
  filters = {},
  page = 1,
  limit = 50,
  sortBy = "age",
  sortDir = "desc",
} = {}) {
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit,
    total: 0,
    totalPages: 0,
  });
  const [summary, setSummary] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const { status, search, merchant } = filters;

  const fetchInvoices = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      const apiSortBy = UI_TO_API_SORT[sortBy] || sortBy;
      // `age` maps to invoiceDate with inverted direction: oldest (highest age)
      // first == earliest invoiceDate first.
      const apiSortDir =
        sortBy === "age" ? (sortDir === "asc" ? "desc" : "asc") : sortDir;
      if (apiSortBy) params.set("sortBy", apiSortBy);
      if (apiSortDir) params.set("sortDir", apiSortDir);
      if (status && status !== "all") params.set("status", status);
      if (search) params.set("search", search);
      if (merchant && merchant !== "all") params.set("merchant", merchant);

      const response = await makeApiRequest(
        "get",
        `${apiRoutes.billingInvoices(provider)}?${params.toString()}`,
        { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
        companyId
      );

      const body = response?.data || {};
      setInvoices(Array.isArray(body.data) ? body.data : []);
      setPagination(
        body.pagination || { page: 1, limit, total: 0, totalPages: 0 }
      );
    } catch (err) {
      setError(err);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    provider,
    page,
    limit,
    sortBy,
    sortDir,
    status,
    search,
    merchant,
    auth?.user?.id,
    auth?.user?.accessToken,
  ]);

  const fetchSummary = useCallback(async () => {
    if (!companyId) return;
    setSummaryLoading(true);
    try {
      const response = await makeApiRequest(
        "get",
        apiRoutes.billingSummary(provider),
        { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
        companyId
      );
      setSummary(response?.data || null);
    } catch (err) {
      // Summary failure is non-fatal — the table can still render.
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [companyId, provider, auth?.user?.id, auth?.user?.accessToken]);

  // Global merchant list (whole collection, NOT page-scoped) so the merchant
  // filter can list every merchant. Fetched once per provider, not per page/filter.
  const fetchMerchants = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await makeApiRequest(
        "get",
        apiRoutes.billingMerchants(provider),
        { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
        companyId
      );
      const body = response?.data || {};
      setMerchants(Array.isArray(body.data) ? body.data : []);
    } catch (err) {
      setMerchants([]); // non-fatal — filter falls back to empty
    }
  }, [companyId, provider, auth?.user?.id, auth?.user?.accessToken]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    fetchMerchants();
  }, [fetchMerchants]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const refetch = useCallback(() => {
    fetchInvoices();
    fetchSummary();
  }, [fetchInvoices, fetchSummary]);

  return {
    invoices,
    pagination,
    summary,
    merchants,
    loading,
    summaryLoading,
    error,
    refetch,
  };
}
